import { Box, CircularProgress } from '@material-ui/core';
import { formatMoney, LoanApplicationStatus } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { LoanApplicationResponseCard } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/components/LoanApplicationResponseCard';
import { LoanApplicationEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/LoanApplication/LoanApplicationEntity';
import { AbstractProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/AbstractProcessingStrategyEntity';
import { useErrorResponse } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/hooks/useErrorResponse';
import { useProcessingStrategy } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/hooks/useProcessingStrategy';
import { ProcessingStatus } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/types/ProcessingStatus';
import { getLoanApplicationStatusTagProps } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/utils/getTagProps';
import { CopyToClipboardButton } from '@/components/shared/components/CopyToClipboardButton';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { StatusTagType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

interface CreditLineApplicationCardProps {
  loanApplication: LoanApplicationEntity;
  processingStrategyEntity: AbstractProcessingStrategyEntity;
  processingStatus: ProcessingStatus | null;
}

export const CreditLineApplicationCard = ({
  loanApplication,
  processingStrategyEntity,
  processingStatus,
}: CreditLineApplicationCardProps) => {
  const { setProcessingStrategyEntity } = useProcessingStrategy();

  const { palette } = useCustomTheme();
  const { ref, width } = useWidth<HTMLDivElement>();
  const { resetErrorResponse } = useErrorResponse({ loanApplication });

  const [processing, setProcessing] = useState(false);
  const [choosing, setChoosing] = useState(false);

  const principal = processingStrategyEntity.loan?.loanConditions.principal;
  const creditLineAmount = principal ? formatMoney(amountToValueMoneyMapper(principal)) : '-';

  const onProcess = async () => {
    setProcessing(true);
    await processingStrategyEntity.processLoanApplication(loanApplication.id);
    setProcessingStrategyEntity(processingStrategyEntity.clone());
    resetErrorResponse();
    setProcessing(false);
  };

  const onSelect = async () => {
    setChoosing(true);
    await processingStrategyEntity.chooseLoanApplication(loanApplication.id);
    setProcessingStrategyEntity(processingStrategyEntity.clone());
    resetErrorResponse();
    setChoosing(false);
  };

  const isSmall = width && width < 400;

  let lenderRequestId = loanApplication.lenderFields.lenderRequestId;
  if (lenderRequestId) {
    lenderRequestId = lenderRequestId.split('_')[0];
  }

  const allowProcessing =
    processingStatus !== 'processing' && processingStrategyEntity.allowProcessing(loanApplication.id);

  const allowChoosing = processingStrategyEntity.allowChoosing(loanApplication.id);

  return (
    <CustomPaper
      ref={ref}
      fill="paper"
      key={`${loanApplication.id}-${loanApplication.lenderName}`}
      style={{
        padding: isSmall ? 10 : 16,
        ...(loanApplication.isSelected() && { boxShadow: `0px 0px 0px 2px ${palette.primary.main}` }),
      }}
    >
      <FlexRow>
        <FlexRow
          alignItems="flex-start"
          justifyContent="flex-start"
          gridGap={isSmall ? 16 : 20}
          flexGrow={1}
          minWidth={0}
        >
          <CustomPaper
            fill="grey.light"
            elevation={0}
            boxProps={{ width: 'auto' }}
            style={{
              height: isSmall ? 16 : 28,
              width: isSmall ? 16 : 28,
              padding: isSmall ? 16 : 12,
              flexGrow: '0 !important',
            }}
          >
            <LenderIcon
              size={isSmall ? 18 : 28}
              lenderName={loanApplication.lenderName}
              isActive={loanApplication.isSelected()}
            />
          </CustomPaper>
          <FlexColumn gridGap={3} flexGrow={1} minWidth={0}>
            <FlexRow gridGap={16} flexGrow={1} width="100%">
              <FlexRow gridGap={isSmall ? 8 : 12} flexGrow={1} justifyContent="flex-start" minWidth={0}>
                <Box minWidth={50} maxWidth={160}>
                  <Font variant="caption" color="textSecondary" display="block">
                    <Text tId="AMOUNT" />
                  </Font>
                  <Font variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {creditLineAmount}
                  </Font>
                </Box>
              </FlexRow>
              <Box flexShrink={0}>
                <LoanApplicationStatusTag loading={processing} loanApplication={loanApplication} />
              </Box>
            </FlexRow>
            <CustomDivider margin={1} />
            <FlexRow width="100%" gridGap={24} height={24}>
              <FlexRow minWidth={0}>
                <Font className="overflow-text-ellipsis" variant="caption" color="textSecondary" display="block">
                  <>
                    <Text tId="ID" />
                    {': '}
                    {lenderRequestId ?? '-'}
                  </>
                </Font>
                {lenderRequestId && (
                  <CopyToClipboardButton style={{ color: palette.text.secondary }} value={lenderRequestId.toString()} />
                )}
              </FlexRow>
              {!processing && !loanApplication.isProcessing && allowProcessing && (
                <FlexRow justifyContent="flex-end" alignItems="center" gridGap={8}>
                  <StandardButton
                    main
                    size="small"
                    loading={processing}
                    onClick={onProcess}
                    style={{ padding: '0 8px', height: 24 }}
                  >
                    <Text tId="PROCESS" />
                  </StandardButton>
                </FlexRow>
              )}
              {!processing && !loanApplication.isProcessing && allowChoosing && lenderRequestId && (
                <FlexRow justifyContent="flex-end" alignItems="center" gridGap={8}>
                  <StandardButton
                    main
                    size="small"
                    loading={choosing}
                    onClick={onSelect}
                    style={{ padding: '0 8px', height: 24 }}
                  >
                    <Text tId="SELECT" />
                  </StandardButton>
                </FlexRow>
              )}
            </FlexRow>
          </FlexColumn>
        </FlexRow>
      </FlexRow>
    </CustomPaper>
  );
};

interface LoanApplicationStatusTagProps {
  loanApplication: LoanApplicationEntity;
  loading: boolean;
}

const LoanApplicationStatusTag = ({ loanApplication, loading }: LoanApplicationStatusTagProps) => {
  if (loading) {
    return (
      <Box pr={1}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (loanApplication.status) {
    return <StatusTag {...getLoanApplicationStatusTagProps(loanApplication.status)} />;
  }

  if (!loanApplication.state || loanApplication.state === LoanApplicationStatus.NOT_PROCESSED) {
    if (loanApplication.isProcessing) {
      return (
        <Box pr={1}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    return <StatusTag type={StatusTagType.DEFAULT} colorType="light" labelTextId="NOT_PROCESSED" />;
  }

  return <LoanApplicationResponseCard loanApplication={loanApplication} />;
};
